import React from "react"
import {useNavigate} from "react-router-dom"
import TitleBar, {MetaItemProps, TitleBarAction} from "../../components/TitleBar/TitleBar"
import {MainSection} from "../../containers/MainSection/MainSection"
import {
	ApplicationDocument,
	ApplicationType,
	ApplicationTypeIcons,
	isApplicationDocument,
	Officer,
} from "../../resources/application"
import {getProductApplicationActions} from "../../resources/applicationActions"
import {
	CreatedAtTag,
	UpdatedAtTag,
	Tags,
	AssigneeTag,
	ProductApplicationStatusTag,
	EvaluationOutcomeTag,
} from "../../components/Tag"
import BeneficialOwners from "../../components/BeneficialOwners/BeneficialOwners"
import {OkDocument, Resource} from "../../resources/common"
import {
	BeneficialOwnerResource,
	isBeneficialOwner,
	calculateBeneficialOwnersStatus,
} from "../../resources/beneficialOwner"
import {isOrg, Org} from "../../resources/org"
import {getAuditLogEventsHeaderMetaItems} from "../Applications/auditLogMetaItems"
import {useAuditLog} from "../../components/AuditLogDrawer/useAuditLog"
import AuditLogDrawer from "../../components/AuditLogDrawer/AuditLogDrawer"
import AuditLogActionButton from "../../components/AuditLogActionButton/AuditLogActionButton"
import {
	BusinessProductApplication,
	businessProductApplicationPreApprovedLink,
	getProductApplicationName,
	isOfficer,
	ProductApplicationStatus,
	reevaluableProductApplicationStatuses,
} from "../../resources/productApplication"
import ApplicationDocuments from "../../components/ApplicationDocuments/ApplicationDocuments"
import ProductApplicationAdditionalInfo from "../../components/ProductApplicationAdditionalInfo/ProductApplicationAdditionalInfo"
import ContactBusinessProductApplication from "../../components/ContactBusinessProductApplication/ContactBusinessProductApplication"
import InformationBusinessProductApplication from "../../components/BusinessInformationProductApplication/InformationBusinessProductApplication"
import OfficerInformationBusinessProductApplication from "../../components/OfficerInformationBusinessProductApplication/OfficerInformationBusinessProductApplication"
import EvaluationFlagsBusinessProductApplication from "../../components/EvaluationFlagsBusinessProductApplication/EvaluationFlagsBusinessProductApplication"
import {
	useAccessToken,
	useIsUnitComplianceUser,
	useIsBankOperationsUser,
	useIsBankReadOnlyUser,
} from "../../services/auth"
import {useModal} from "react-modal-hook"
import Button from "../../components/Button/Button"
import LinkButton from "../../components/LinkButton/LinkButton"
import {HasPermission} from "../../containers/PermissionedUser/Permission"
import ReevaluateProductApplicationChooseEntityModal from "../../components/ReevaluateProductApplication/ReevaluateProductApplicationChooseEntityModal/ReevaluateProductApplicationChooseEntityModal"
import Icon from "../../components/Icon/Icon"
import {ApproveProductApplicationModal} from "../../components/ApplicationApprove/ProductApplicationApprove"
import {CancelProductApplicationModal} from "../../components/ApplicationCancel/ProductApplicationCancel"
import {DenyProductApplicationModal} from "../../components/ApplicationDeny/ProductApplicationDeny"
import {ProductBusinessRequestDocumentModal} from "../../components/RequestDocumentModal/ProductApplicationRequestDocumentModal"
import TagsCard from "../../components/Tags/TagsCard"
import {useListReturnPath} from "../../hooks/useListReturnPath"
import {ProductApplicationDecision} from "../../components/ApplicationDecision/ProductApplicationDecision"
import {CompletePendingModal} from "../../components/ApplicationCompletePending/ApplicationCompletePendingModal"
import CompletePendingButton from "../../components/ApplicationCompletePendingButton/CompletePendingButton"
import {ProductApplicationAllowedToViewEvaluation} from "../../containers/PermissionedUser/PermissionedUser"

function BusinessProductApplicationInternal({
	business,
	refresh,
	documents,
	beneficialOwners,
	included,
}: {
	business: BusinessProductApplication
	documents: ApplicationDocument[]
	beneficialOwners?: BeneficialOwnerResource[]
	refresh: () => void
	included?: Resource[]
}) {
	const listReturnPath = useListReturnPath("/applications/#tab=productApplication")
	const {
		attributes,
		attributes: {contact},
	} = business
	const navigate = useNavigate()
	const officer = included?.find((r) => isOfficer(r) && r.id == business.relationships.officer?.data.id)
		?.attributes as Officer
	const org = included?.find((r) => isOrg(r) && r.id == business.relationships.org.data.id) as Org

	const boStatus = calculateBeneficialOwnersStatus(beneficialOwners)

	const canReevaluate = reevaluableProductApplicationStatuses.includes(attributes.status)

	const [showChooseEntityModal, closeChooseEntityModal] = useModal(() => (
		<ReevaluateProductApplicationChooseEntityModal
			close={closeChooseEntityModal}
			onSuccess={refresh}
			application={business}
			officer={officer}
			beneficialOwners={beneficialOwners}
			navigate={navigate}
		/>
	))

	const name = getProductApplicationName(business)
	const preApprovedProductApplicationLink = businessProductApplicationPreApprovedLink(business)
	const {showAuditLog, openAuditLogDrawer, closeAuditLogDrawer, isAuditLogDrawerOpened} = useAuditLog()
	const auditLogEventsHeaderMetaItems = getAuditLogEventsHeaderMetaItems({
		applicationName: attributes.name,
		applicationType: ApplicationType.business,
		pageIcon: ApplicationTypeIcons[ApplicationType.business],
		org: org,
	})
	const accessToken = useAccessToken()
	const defaultInformationHidingState =
		!useIsUnitComplianceUser() && !useIsBankOperationsUser() && !useIsBankReadOnlyUser()

	const {canApprove, canDeny, canCancel} = getProductApplicationActions(attributes.status)

	const [showApprove, hideApprove] = useModal(() => (
		<ApproveProductApplicationModal close={hideApprove} application={business} onSuccess={refresh} />
	))

	const [showDeny, hideDeny] = useModal(() => (
		<DenyProductApplicationModal
			close={hideDeny}
			application={business}
			onSuccess={refresh}
			accessToken={accessToken}
			resources={included}
		/>
	))

	const [showCancel, hideCancel] = useModal(() => (
		<CancelProductApplicationModal close={hideCancel} application={business} onSuccess={refresh} />
	))
	const [showRequestDocument, hideRequestDocument] = useModal(() => (
		<ProductBusinessRequestDocumentModal
			application={business}
			officer={officer}
			beneficialOwners={beneficialOwners}
			close={hideRequestDocument}
			onSuccess={refresh}
		/>
	))
	const [showCompletePending, hideCompletePending] = useModal(() => (
		<CompletePendingModal close={hideCompletePending} application={business} onSuccess={refresh} />
	))

	const customerLink = `/customers/business/${business.relationships.customer.data.id}`
	const metaTitleBar: MetaItemProps[] = [
		{text: "Business", icon: "toolbox"},
		{
			text: "Customer",
			icon: "user-geometric-action-money-fund---users",
			path: customerLink,
		},
	]

	return (
		<>
			<TitleBar title={name} breadcrumbs={[{text: "Product Applications", path: listReturnPath}]} meta={metaTitleBar}>
				{showAuditLog && <AuditLogActionButton onClick={openAuditLogDrawer} />}
				<HasPermission resource="productApplication" action="update">
					<TitleBarAction>
						<Button
							tooltipPlace="bottom"
							disabled={!canReevaluate}
							theme="neutral"
							variant="outline"
							onClick={showChooseEntityModal}
							prefixIcon="pencil-1--interface-essential"
							className="mr-2"
						>
							<span>Edit and Re-submit</span>
						</Button>
					</TitleBarAction>
				</HasPermission>
				<TitleBarAction>
					<HasPermission resource="productApplication.status.cancel" action="update">
						{canCancel && (
							<button className="button button-cancel mr-0 is-flex is-align-items-center" onClick={showCancel}>
								<Icon icon={"user-geometric-action-cross-remove"} size={16} />
								<span>Cancel Application</span>
							</button>
						)}
					</HasPermission>
				</TitleBarAction>
				<TitleBarAction>
					<CompletePendingButton application={business} buttonOnClick={showCompletePending} />
				</TitleBarAction>
			</TitleBar>

			<MainSection>
				<AuditLogDrawer
					open={isAuditLogDrawerOpened}
					onClose={closeAuditLogDrawer}
					auditLogEventsHeaderMetaItems={auditLogEventsHeaderMetaItems}
					resourceType="productApplication"
					resourceId={business.id}
					showSubResourcesId={true}
				/>
				<div className="columns is-mobile">
					<div className="column is-narrow">
						<div className="buttons">
							{attributes.evaluationEntityId ? (
								<LinkButton to={`https://unit.app.alloy.com/entities/${attributes.evaluationEntityId}`}>
									Alloy
								</LinkButton>
							) : null}
						</div>
					</div>
					<div className="column">
						<HasPermission resource="productApplication.status" action="update">
							<div className="buttons is-right">
								{attributes.status === ProductApplicationStatus.AwaitingDocuments ||
								attributes.status === ProductApplicationStatus.PendingReview ? (
									<button className="button is-info" onClick={() => showRequestDocument()}>
										Request Documents
									</button>
								) : null}
								<button className="button is-danger" disabled={!canDeny} onClick={() => showDeny()}>
									Deny
								</button>

								<button className="button is-success" disabled={!canApprove} onClick={() => showApprove()}>
									Approve
								</button>
							</div>
						</HasPermission>
					</div>
				</div>
				<div className="columns">
					<div className="column">
						<Tags>
							<CreatedAtTag createdAt={attributes.createdAt} />
							<UpdatedAtTag updatedAt={attributes.updatedAt} />
							{attributes.assignee ? <AssigneeTag assignee={attributes.assignee} /> : <React.Fragment />}
							<ProductApplicationStatusTag status={attributes.status} />
							<ProductApplicationAllowedToViewEvaluation>
								{attributes.evaluationOutcome && (
									<EvaluationOutcomeTag value={attributes.evaluationOutcome} label="Business Evaluation" />
								)}
								{officer && officer.status && (
									<EvaluationOutcomeTag value={officer.status} label="Officer Evaluation" />
								)}
								{beneficialOwners && <EvaluationOutcomeTag value={boStatus} label="Beneficial Owners Evaluation" />}
							</ProductApplicationAllowedToViewEvaluation>
						</Tags>
					</div>
				</div>
				<div className="columns">
					<div className="column is-3">
						<ProductApplicationDecision application={business} />
						<div className="columns">
							<div className="column">
								<ProductApplicationAdditionalInfo
									requestedProduct={attributes.requestedProduct}
									decisionMethod={attributes.decisionMethod}
									preApprovedEvaluatedAt={attributes.preApprovedEvaluatedAt}
									orgId={business.relationships.org.data.id}
									included={included}
									productApplicationPreApprovedLink={preApprovedProductApplicationLink}
								/>
							</div>
						</div>
						<div className="columns">
							<div className="column">
								<ContactBusinessProductApplication
									contact={contact}
									defaultInformationHidingState={defaultInformationHidingState}
								/>
							</div>
						</div>
						<div className="columns">
							<div className="column">
								<InformationBusinessProductApplication
									business={business}
									defaultInformationHidingState={defaultInformationHidingState}
									included={included}
								/>
							</div>
						</div>
						<div className="columns">
							<div className="column">
								<OfficerInformationBusinessProductApplication
									officer={officer}
									defaultInformationHidingState={defaultInformationHidingState}
								/>
							</div>
						</div>
						<div className="columns">
							<div className="column">
								<TagsCard tags={attributes.tags} editable={false} />
							</div>
						</div>
					</div>
					<div className="column">
						<div className="columns">
							<div className="column">
								<EvaluationFlagsBusinessProductApplication
									application={business}
									officer={officer}
									beneficialOwners={beneficialOwners}
								/>
							</div>
						</div>
						<div className="columns">
							<div className="column">
								<ApplicationDocuments application={business} documents={documents} refresh={refresh} />
							</div>
						</div>
						<div className="columns">
							<div className="column">
								<BeneficialOwners refresh={refresh} beneficialOwners={beneficialOwners || []} readOnly={true} />
							</div>
						</div>
					</div>
				</div>
			</MainSection>
		</>
	)
}

export default function BusinessProductApplicationPage({
	productApplicationDocument,
	refresh,
}: {
	productApplicationDocument: OkDocument<BusinessProductApplication>
	refresh: () => void
}) {
	return (
		<BusinessProductApplicationInternal
			business={productApplicationDocument.data}
			documents={productApplicationDocument.included?.filter(isApplicationDocument) || []}
			beneficialOwners={productApplicationDocument.included?.filter(isBeneficialOwner)}
			refresh={refresh}
			included={productApplicationDocument.included}
		/>
	)
}
